/* eslint-disable react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types'
import { Transition } from 'react-transition-group';
import cx from 'classnames';
import { throttle } from 'lodash';

import Portal from '../Portal';

const TOOLTIP_MARGIN = 8;
const TIMEOUT = 100;
const DEFAULT_STYLE = {
    transition: `all 300ms ease-out`,
    opacity: 0
};
const POSITION_STYLE = {
    bottom: {
        transform: 'translate(0, 4px)'
    },
    top: {
        transform: 'translate(0, -4px)'
    },
    right: {
        transform: 'translate(4px, 0)'
    },
    left: {
        transform: 'translate(-4px, 0)'
    },
    bottomLeft: {
        transform: 'translate(0, 4px)'
    },
    bottomRight: {
        transform: 'translate(0, 4px)'
    },
    topLeft: {
        transform: 'translate(0, -4px)'
    },
    topRight: {
        transform: 'translate(0, -4px)'
    }
};
const TRANSITION_STYLE = {
    entering: { opacity: 0 },
    entered: {
        opacity: 1,
        transform: 'translate(0, 0)'
    }
};

export default class TooltipPopup extends React.PureComponent {
    static propTypes = {
        id: PropTypes.string.isRequired,

        /** The content to display inside the popup */
        content: PropTypes.node.isRequired,

        /** The desired popup position */
        position: PropTypes.oneOf(['top', 'bottom', 'right', 'left', 'topLeft', 'topRight']),

        /** The maximum width of the popup, in pixels */
        maxWidth: PropTypes.number,

        isVisible: PropTypes.bool,

        triggerRef: PropTypes.shape({
            current: PropTypes.any
        })
    };

    static defaultProps = {
        position: 'top',
        maxWidth: 250
    };

    constructor(props) {
        super(props);
        this.tooltipRef = React.createRef();

        this.state = {
            tooltipRect: null
        };
    }

    componentDidMount() {
        window.addEventListener('keyup', this.handleKeyUp);
        window.addEventListener('resize', this.handleWindowResize);
    }

    componentDidUpdate(prevProps) {
        const { content } = this.props;
        const { tooltipRect } = this.state;

        if (!tooltipRect || content !== prevProps.content) {
            if (this.tooltipRef.current) {
                this.setState({
                    tooltipRect: this.tooltipRef.current.getBoundingClientRect()
                });
            }
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keyup', this.handleKeyUp);
        window.removeEventListener('resize', this.handleWindowResize);
    }

    handleKeyUp = (event) => {
        const key = event.key || event.keyCode;
        const { isVisible } = this.state;

        switch (key) {
            case 'Escape' || 'Esc' || 27:
                if (isVisible) {
                    this.hideTooltip();
                }
                break;
            default:
                break;
        }
    };

    handleWindowResize = throttle(() => {
        const { isVisible } = this.props;

        if (isVisible) {
            this.setState({
                tooltipRect: null
            });
        }
    }, 100)

    render() {
        const { content, position, maxWidth, isVisible, triggerRef, id } = this.props;
        const { tooltipRect } = this.state;

        // First renders a hidden tooltip to get the measurements, then re-renders
        // calculating the correct positioning

        // bail if it's hidden
        if (!isVisible) return null;

        // if no rect data, then draw hidden tooltip to measure
        if (!tooltipRect) {
            return (
                <Portal>
                    <div
                        className="sarsa-tooltip"
                        style={{ visibility: "none", maxWidth }}
                        ref={this.tooltipRef}>
                        {content}
                    </div>
                </Portal>
            );
        }

        // If there's not a triggerRef that points to a DOM node, then the user
        // supplied an incompatible child.
        if (!triggerRef.current || !triggerRef.current.getBoundingClientRect) {
            // eslint-disable-next-line
            console.warn('Tooltip requires a single child that can be assigned a ref. Supply either an html element or a React component with a innerRef prop that is passed to an html element.');
            return null;
        }

        const bodyRect = document.body.getBoundingClientRect();
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const widthDifference = (triggerRect.width - tooltipRect.width) / 2;
        const heightDifference = (triggerRect.height - tooltipRect.height) / 2;
        const collisions = {
            top: triggerRect.top - tooltipRect.height < 0,
            bottom: window.innerHeight < triggerRect.bottom + tooltipRect.height + TOOLTIP_MARGIN,
            left: triggerRect.left - tooltipRect.width < 0,
            right: window.innerWidth < triggerRect.right + tooltipRect.width + TOOLTIP_MARGIN,
            heightTop: triggerRect.top - (tooltipRect.height / 2) + (triggerRect.height / 2) < 0,
            heightBottom: window.innerHeight < triggerRect.bottom + (tooltipRect.height / 2) - (triggerRect.height / 2),
            widthLeft: triggerRect.left - (tooltipRect.width / 2) + (triggerRect.width / 2) < 0,
            widthRight: window.innerWidth < triggerRect.right + (tooltipRect.width / 2) - (triggerRect.width / 2),
            alignedLeftWidth: window.innerWidth < triggerRect.left + tooltipRect.width,
            alignedRightWidth: triggerRect.left - tooltipRect.width < 0
        }

        let finalPosition;
        let top;
        let left;

        // Figure out a plan B (or C or D or E) if there are collision(s)
        switch (position) {
            case 'top':
                if (!collisions.top && !collisions.widthLeft && !collisions.widthRight) {
                    finalPosition = 'top';
                    break;
                }
                if (!collisions.top && !collisions.alignedLeftWidth) {
                    finalPosition = 'topLeft';
                    break;
                }
                if (!collisions.top && !collisions.alignedRightWidth) {
                    finalPosition = 'topRight';
                    break;
                }
                if (collisions.top && !collisions.widthLeft && !collisions.widthRight) {
                    finalPosition = 'bottom';
                    break;
                }
                if (collisions.top && !collisions.alignedLeftWidth) {
                    finalPosition = 'bottomLeft';
                    break;
                }
                if (collisions.top && !collisions.alignedRightWidth) {
                    finalPosition = 'bottomRight';
                    break;
                }
                finalPosition = 'top';
                break;
            case 'topRight':
                if (!collisions.top && !collisions.alignedRightWidth) {
                    finalPosition = 'topRight';
                    break;
                }
                if (!collisions.top && collisions.alignedRightWidth) {
                    finalPosition = 'topLeft';
                    break;
                }
                if (collisions.top && !collisions.alignedRightWidth) {
                    finalPosition = 'bottomRight';
                    break;
                }
                if (collisions.top && collisions.alignedRightWidth) {
                    finalPosition = 'bottomLeft';
                    break;
                }
                finalPosition = 'top';
                break;
            case 'topLeft':
                if (!collisions.top && !collisions.alignedLeftWidth) {
                    finalPosition = 'topLeft';
                    break;
                }
                if (!collisions.top && collisions.alignedLeftWidth) {
                    finalPosition = 'topRight';
                    break;
                }
                if (collisions.top && !collisions.alignedLeftWidth) {
                    finalPosition = 'bottomLeft';
                    break;
                }
                if (collisions.top && collisions.alignedLeftWidth) {
                    finalPosition = 'bottomRight';
                    break;
                }
                finalPosition = 'top';
                break;
            case 'bottom':
                if (!collisions.bottom && !collisions.widthLeft && !collisions.widthRight) {
                    finalPosition = 'bottom';
                    break;
                }
                if (!collisions.bottom && !collisions.alignedLeftWidth) {
                    finalPosition = 'bottomLeft';
                    break;
                }
                if (!collisions.bottom && !collisions.alignedRightWidth) {
                    finalPosition = 'bottomRight';
                    break;
                }
                if (collisions.bottom && !collisions.widthLeft && !collisions.widthRight) {
                    finalPosition = 'top';
                    break;
                }
                if (collisions.bottom && !collisions.alignedLeftWidth) {
                    finalPosition = 'topLeft';
                    break;
                }
                if (collisions.bottom && !collisions.alignedRightWidth) {
                    finalPosition = 'topRight';
                    break;
                }
                finalPosition = 'top';
                break;
            case 'right':
                if (!collisions.right && !collisions.heightTop && !collisions.heightBottom) {
                    finalPosition = 'right';
                    break;
                }
                if (collisions.right && !collisions.left && !collisions.heightTop && !collisions.heightBottom) {
                    finalPosition = 'left';
                    break;
                }
                if (!collisions.bottom && !collisions.widthLeft && !collisions.widthRight) {
                    finalPosition = 'bottom';
                    break;
                }
                if (!collisions.bottom && !collisions.alignedLeftWidth) {
                    finalPosition = 'bottomLeft';
                    break;
                }
                if (!collisions.bottom && !collisions.alignedRightWidth) {
                    finalPosition = 'bottomRight';
                    break;
                }
                if (!collisions.top && !collisions.widthLeft && !collisions.widthRight) {
                    finalPosition = 'top';
                    break;
                }
                if (!collisions.top && collisions.widthLeft) {
                    finalPosition = 'topLeft';
                    break;
                }
                if (!collisions.top && collisions.widthRight) {
                    finalPosition = 'topRight';
                    break;
                }
                finalPosition = 'top';
                break;
            case 'left':
                if (!collisions.left && !collisions.heightTop && !collisions.heightBottom) {
                    finalPosition = 'left';
                    break;
                }
                if (collisions.left && !collisions.right && !collisions.heightTop && !collisions.heightBottom) {
                    finalPosition = 'right';
                    break;
                }
                if (!collisions.bottom && !collisions.widthLeft && !collisions.widthRight) {
                    finalPosition = 'bottom';
                    break;
                }
                if (!collisions.bottom && !collisions.alignedLeftWidth) {
                    finalPosition = 'bottomLeft';
                    break;
                }
                if (!collisions.bottom && !collisions.alignedRightWidth) {
                    finalPosition = 'bottomRight';
                    break;
                }
                if (!collisions.top && !collisions.widthLeft && !collisions.widthRight) {
                    finalPosition = 'top';
                    break;
                }
                if (!collisions.top && !collisions.alignedLeftWidth) {
                    finalPosition = 'topLeft';
                    break;
                }
                if (!collisions.top && !collisions.alignedRightWidth) {
                    finalPosition = 'topRight';
                    break;
                }
                finalPosition = 'top';
                break;
            default:
                finalPosition = position;
                break;
        }

        switch (finalPosition) {
            case 'right':
                top =
                    triggerRect.bottom -
                    bodyRect.top -
                    triggerRect.height +
                    heightDifference;
                left = triggerRect.right + TOOLTIP_MARGIN;
                break;
            case 'left':
                top =
                    triggerRect.bottom -
                    bodyRect.top -
                    triggerRect.height +
                    heightDifference;
                left = triggerRect.left - tooltipRect.width - TOOLTIP_MARGIN;
                break;
            case 'top':
                top = triggerRect.top - bodyRect.top - tooltipRect.height - TOOLTIP_MARGIN;
                left = triggerRect.left - bodyRect.left + widthDifference;
                break;
            case 'bottom':
                top = triggerRect.bottom - bodyRect.top + TOOLTIP_MARGIN;
                left = triggerRect.left - bodyRect.left + widthDifference;
                break;
            case 'bottomLeft':
                top = triggerRect.bottom - bodyRect.top + TOOLTIP_MARGIN;
                left = triggerRect.left;
                break;
            case 'bottomRight':
                top = triggerRect.bottom - bodyRect.top + TOOLTIP_MARGIN;
                left = triggerRect.right - tooltipRect.width;
                break;
            case 'topLeft':
                top = triggerRect.top - bodyRect.top - tooltipRect.height - TOOLTIP_MARGIN;
                left = triggerRect.left;
                break;
            case 'topRight':
                top = triggerRect.top - bodyRect.top - tooltipRect.height - TOOLTIP_MARGIN;
                left = triggerRect.right - tooltipRect.width;
                break;
            default:
                break;
        }

        const style = { top, left, width: tooltipRect.width };

        return (
            <Portal>
                <Transition in appear timeout={TIMEOUT}>
                    {(status) => (
                        <span
                            className={cx('sarsa-tooltip', finalPosition)}
                            data-component="Tooltip"
                            role="tooltip"
                            style={{
                                ...style,
                                ...DEFAULT_STYLE,
                                ...POSITION_STYLE[finalPosition],
                                ...TRANSITION_STYLE[status]
                            }}
                            id={id}>
                            {content}
                        </span>
                    )}
                </Transition>
            </Portal>
        );
    }
}